import type { PresentationData } from '../types'
import { entwicklerschulungTag1 } from './entwicklerschulung-tag1'
import { developerTrainingDay1 } from './developer-training-day1'
import { contextEngineeringProfessionalDe } from './context-engineering-professional-de'
import { contextEngineeringProfessionalEn } from './context-engineering-professional-en'
import { professionalLayoutsDemo } from './professional-layouts-demo'
import { erstgespraech } from './erstgespraech'
import { erstgespraechEn } from './erstgespraech-en'
import { presentationService } from '../services/PresentationService'
import { API_CONFIG } from '../config/api.config'

/**
 * Presentation Registry
 *
 * Add new presentations here to make them available in the library.
 * Each entry should have a unique slug (used in URLs) and the presentation data.
 *
 * Static presentations serve as fallback when backend API is unavailable or disabled.
 */

export interface PresentationEntry {
  slug: string
  presentation: PresentationData
}

/**
 * Static presentations (fallback when backend is unavailable)
 * These presentations are always available and will be used if:
 * - Backend API is disabled (VITE_USE_BACKEND=false)
 * - Backend API is unreachable
 * - A specific presentation is not found in the backend
 */
export const staticPresentations: PresentationEntry[] = [
  {
    slug: 'professional-layouts-demo',
    presentation: professionalLayoutsDemo,
  },
  {
    slug: 'entwicklerschulung-tag1',
    presentation: entwicklerschulungTag1,
  },
  {
    slug: 'developer-training-day1',
    presentation: developerTrainingDay1,
  },
  {
    slug: 'context-engineering-professional-de',
    presentation: contextEngineeringProfessionalDe,
  },
  {
    slug: 'context-engineering-professional-en',
    presentation: contextEngineeringProfessionalEn,
  },
  {
    slug: 'erstgespraech',
    presentation: erstgespraech,
  },
  {
    slug: 'erstgespraech-en',
    presentation: erstgespraechEn,
  },
  // Add more presentations here:
  // {
  //   slug: 'my-presentation',
  //   presentation: myPresentation,
  // },
]

/**
 * Load presentations from backend or fallback to static presentations
 *
 * This function:
 * 1. Attempts to load presentations from the backend API (if enabled)
 * 2. Merges backend presentations with static presentations (static override on conflicts)
 * 3. Falls back to static presentations only if backend is disabled or unavailable
 *
 * @returns Promise resolving to an array of presentation entries
 */
export async function loadPresentations(): Promise<PresentationEntry[]> {
  // If backend is explicitly disabled, return static presentations
  if (!API_CONFIG.USE_BACKEND) {
    return staticPresentations
  }

  try {
    const result = await presentationService.fetchLibrary()

    if (result.success && result.data) {
      // Merge backend presentations with static fallbacks
      // Static presentations override backend ones on slug conflicts
      const mergedMap = new Map<string, PresentationData>()

      // Add backend presentations first
      for (const entry of result.data) {
        mergedMap.set(entry.slug, entry.data)
      }

      // Override/add static presentations
      for (const entry of staticPresentations) {
        mergedMap.set(entry.slug, entry.presentation)
      }

      // Convert back to array
      return Array.from(mergedMap.entries()).map(([slug, presentation]) => ({
        slug,
        presentation,
      }))
    }
  } catch (error) {
    // Log error but continue with static fallback
    if (import.meta.env.DEV) {
      console.warn('Failed to load presentations from backend, using static fallback:', error)
    }
  }

  // Fallback to static presentations only
  return staticPresentations
}

/**
 * Load a single presentation by slug
 *
 * Tries to fetch from backend first (if enabled), falls back to static presentations.
 *
 * @param slug - The presentation slug
 * @returns Promise resolving to presentation data or null if not found
 */
export async function loadPresentationBySlug(slug: string): Promise<PresentationData | null> {
  // Check static presentations first (fast path)
  const staticEntry = staticPresentations.find(entry => entry.slug === slug)
  if (staticEntry) {
    return staticEntry.presentation
  }

  // If backend is enabled, try fetching from there
  if (API_CONFIG.USE_BACKEND) {
    try {
      const result = await presentationService.fetchBySlug(slug)
      if (result.success && result.data) {
        return result.data
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn(`Failed to load presentation "${slug}" from backend:`, error)
      }
    }
  }

  return null
}

/**
 * Legacy synchronous export for backward compatibility
 * @deprecated Use loadPresentations() instead for backend integration
 */
export const presentations: PresentationEntry[] = staticPresentations

/**
 * Legacy synchronous lookup for backward compatibility
 * @deprecated Use loadPresentationBySlug() instead for backend integration
 */
export const presentationsBySlug: Record<string, PresentationData> = staticPresentations.reduce(
  (acc, { slug, presentation }) => {
    acc[slug] = presentation
    return acc
  },
  {} as Record<string, PresentationData>
)
